import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { EmteriaRoles } from "src/data/roles";
import i18next from "src/i18n/config";

import { fetchAllBuilds } from "@admin/api/build";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import PageHeader from "@dashboard/shared/components/PageHeader";
import { Tag } from "@dashboard/shared/components/Tag/Tag";
import { Box, TableRow, Typography, useTheme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { withRequiredRole } from "@navigation/utils/withRequiredRole";
import TimeService from "@services/timeService/timeService";
import { LoadingBox } from "@shared/Loading";
import { useFullPageTableLayoutStyles } from "@shared/table/FullPageTableLayout.style";
import {
    MinimalTable,
    MinimalTableCell,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
} from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import { showErrorToast } from "@shared/toasts/Toasts";
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";

import TableItem from "./TableItem";

type Status = "pending" | "fulfilled" | "rejected" | "uninitialized";
export type State = {
    data: API.AdminBuild[];
    count: number;
    status: Status;
};
const defaultState: State = {
    data: [],
    count: 0,
    status: "uninitialized",
};

const getStatus = (buildStatus: API.AdminBuild["buildStatus"] | null) => {
    switch (buildStatus) {
        case "running":
            return { icon: "fas fa-spinner fa-spin", tooltip: "Build is running" };
        case "pending":
            return { icon: "fas fa-hourglass-half", tooltip: "Build is pending" };
        default:
            return { icon: "fas fa-question-circle", tooltip: "Unknown status" };
    }
};

const columnHelper = createColumnHelper<API.AdminBuild>();

const tableColumns = [
    columnHelper.accessor("buildStatus", {
        id: "buildStatus",
        header: "Status",
        cell: (info) => (
            <MinimalTableCell>
                <i className={clsx(getStatus(info.getValue()).icon)} />
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("buildId", {
        id: "buildId",
        header: "ID",
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("codename", {
        id: "codename",
        header: "Codename",
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("version", {
        id: "version",
        header: "Version",
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("created", {
        id: "created",
        header: "Created",
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{TimeService.getLocalDate(info.getValue(), "ISO", "dateTime") ?? "-"}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: true,
    }),
    columnHelper.accessor("metadata.user.tags", {
        id: "tags",
        header: "Tags",
        cell: (info) => {
            const theme = useTheme();
            const tags = info.getValue() || [];
            const hasTags = tags.length > 0;

            return (
                <MinimalTableCell>
                    {hasTags ? (
                        <div style={{ display: "flex", gap: theme.spacing(1), flexWrap: "wrap" }}>
                            {tags.map((tag: { key: string; value: string }) => (
                                <Tag key={tag.key} item={tag} />
                            ))}
                        </div>
                    ) : (
                        <Typography>-</Typography>
                    )}
                </MinimalTableCell>
            );
        },
        enableSorting: true,
    }),
];

const AdminBuildsPage = () => {
    const [state, setState] = React.useState<State>(defaultState);

    const { classes: layoutClasses, cx } = useFullPageTableLayoutStyles();
    const { t } = useTranslation(["pages"]);

    const table = useReactTable<API.AdminBuild>({
        data: state.data,
        columns: tableColumns,
        initialState: {
            sorting: [{ id: "version", desc: true }],
            pagination: {
                pageIndex: 0,
                pageSize: 12,
            },
        },
        // turn off client-side pagination
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });
    const { pageIndex, pageSize } = table.getState().pagination;

    const { ref: tableContainerRef, contentOverflows } = useContainerOverflow(true, [pageIndex, pageSize, state.data.length]);

    React.useLayoutEffect(() => {
        setState((prev) => ({ ...prev, status: "pending" }));
        void fetchAllBuilds()
            .then((data) => setState({ data: data.list, count: data.count, status: "fulfilled" }))
            .catch((error) => {
                const err = error as Error;
                showErrorToast(err.message);
                setState((prev) => ({ ...prev, status: "rejected" }));
            });
    }, [pageIndex, pageSize]);

    if (state.status === "uninitialized") {
        return <LoadingBox />;
    }

    return (
        <PageContainer fitViewport>
            <PageHeader title="Build queue status" description={t("pages:adminBuildsPage.description")} />
            <Box className={layoutClasses.tableLayout}>
                <Box className={layoutClasses.tableWrapper}>
                    <MinimalTableContainer
                        ref={tableContainerRef}
                        className={cx(layoutClasses.tableContainer, !contentOverflows && layoutClasses.tableContainerNoScroll)}
                    >
                        <MinimalTable>
                            <MinimalTableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <Header key={header.id} {...header} />
                                        ))}
                                        <MinimalTableHeaderCell />
                                    </TableRow>
                                ))}
                            </MinimalTableHeader>
                            <TableBody
                                table={table}
                                loading={state.status === "pending"}
                                noDataMessage={t("pages:adminBuildsPage.noBuildsInTheQueue")}
                            >
                                {table.getRowModel().rows.map((row) => (
                                    <TableItem key={row.id} setState={setState} row={row} />
                                ))}
                            </TableBody>
                        </MinimalTable>
                    </MinimalTableContainer>
                </Box>
                <CustomTablePagination
                    component="div"
                    className={layoutClasses.pagination}
                    rowsPerPageOptions={[12, 24, 48]}
                    count={state.data.length}
                    rowsPerPage={pageSize}
                    page={pageIndex}
                    onPageChange={(_event, page) => table.setPageIndex(page)}
                    onRowsPerPageChange={(e) => {
                        const size = e.target.value ? Number(e.target.value) : 12;
                        table.setPagination({ pageIndex: 0, pageSize: size });
                    }}
                />
            </Box>
        </PageContainer>
    );
};
export default withRequiredRole(
    withMetaDescription(AdminBuildsPage, {
        title: i18next.t("pages:adminBuildsPage.metaTitle"),
        description: i18next.t("pages:adminBuildsPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaAdminRole],
);
